import { LoadingSpinner as LoadingSpinnerPrime } from "components/LoadingSpinner";
import { ProgressSpinner } from "primereact/progressspinner";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import styled from "styled-components";

const RecommendationsContainer = styled.div`
  margin-top: 0.75rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RecommendedCptLink = styled.button<{ $isLoading?: boolean }>`
  background: none;
  border: none;
  color: var(--primary-500);
  text-decoration: underline;
  cursor: ${({ $isLoading }) => ($isLoading ? "not-allowed" : "pointer")};
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.6 : 1)};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: ${({ $isLoading }) =>
      $isLoading ? "var(--primary-500)" : "var(--primary-600)"};
    text-decoration: ${({ $isLoading }) => ($isLoading ? "underline" : "none")};
  }

  &:focus {
    outline: 2px solid #007ad6;
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

const RecommendedCptText = styled.span`
  color: var(--gray-700);
  font-weight: 500;
  font-size: inherit;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const LoadingSpinner = styled.span`
  display: inline-flex;
  margin-left: 0.25rem;
`;

interface ICptRecommendationsProps {
  onRecommendationClick: (cptCode: string) => void;
  onQuantityClick: (cptCode: string, suggestedQuantity: number) => void;
  loadingCptCode?: string | null;
}

export function CptRecommendations({
  onRecommendationClick,
  onQuantityClick,
  loadingCptCode,
}: ICptRecommendationsProps) {
  const {
    cptRecommendations,
    loadingCptRecommendations,
    cptQuantityRecommendations,
    loadingCptQuantityRecommendations,
  } = useSelector((state: RootState) => state.serviceDetailsSlice);

  // Group paired recommendations by the CPT code each is paired with
  const groupedPaired = cptRecommendations.reduce((acc, recommendation) => {
    recommendation.pairedWith.forEach((pairedCode) => {
      if (!acc[pairedCode]) {
        acc[pairedCode] = [];
      }
      acc[pairedCode].push(recommendation);
    });
    return acc;
  }, {} as Record<string, typeof cptRecommendations>);

  // Index quantity recommendations by CPT code for O(1) lookup
  const quantityByCode = cptQuantityRecommendations.reduce((acc, rec) => {
    acc[rec.cptCode] = rec;
    return acc;
  }, {} as Record<string, (typeof cptQuantityRecommendations)[number]>);

  if (loadingCptRecommendations || loadingCptQuantityRecommendations) {
    return (
      <LoadingSpinnerPrime customStyle={{ width: "16px", height: "16px" }} />
    );
  }

  // Unified ordered list of CPT codes that have either a quantity or paired suggestion
  const seen = new Set<string>();
  const orderedCodes: string[] = [];
  Object.keys(groupedPaired).forEach((code) => {
    if (!seen.has(code)) {
      seen.add(code);
      orderedCodes.push(code);
    }
  });
  cptQuantityRecommendations.forEach((rec) => {
    if (!seen.has(rec.cptCode)) {
      seen.add(rec.cptCode);
      orderedCodes.push(rec.cptCode);
    }
  });

  if (orderedCodes.length === 0) {
    return null;
  }

  return (
    <RecommendationsContainer>
      {orderedCodes.map((existingCode) => {
        const quantityRec = quantityByCode[existingCode];
        const pairedRecs = groupedPaired[existingCode];
        const quantityIsLoading =
          quantityRec && loadingCptCode === existingCode;

        return (
          <div className="text-sm font-normal" key={existingCode}>
            <span className="font-semibold">{existingCode}</span>{" "}
            {quantityRec && (
              <>
                <span className="text-gray-700">Common Quantity: </span>
                <RecommendedCptLink
                  $isLoading={!!quantityIsLoading}
                  onClick={() =>
                    onQuantityClick(existingCode, quantityRec.suggestedQuantity)
                  }
                  disabled={!!quantityIsLoading}
                >
                  {quantityRec.suggestedQuantity}
                  {quantityIsLoading && (
                    <LoadingSpinner>
                      <ProgressSpinner
                        style={{ width: "12px", height: "12px" }}
                        strokeWidth="4"
                      />
                    </LoadingSpinner>
                  )}
                </RecommendedCptLink>{" "}
                <span className="text-gray-500">
                  ({quantityRec.occurrenceRate}%)
                </span>
                {pairedRecs && pairedRecs.length > 0 && ", "}
              </>
            )}
            {pairedRecs && pairedRecs.length > 0 && (
              <>
                Commonly paired with:{" "}
                {pairedRecs.map((recommendation, index) => {
                  const isLoading = loadingCptCode === recommendation.cptCode;
                  const canClick = recommendation.existsForPayer && !isLoading;

                  return (
                    <span key={recommendation.cptCode}>
                      {index > 0 && ", "}
                      {canClick ? (
                        <RecommendedCptLink
                          $isLoading={isLoading}
                          onClick={() =>
                            onRecommendationClick(recommendation.cptCode)
                          }
                          disabled={isLoading}
                        >
                          {recommendation.cptCode}
                          {isLoading && (
                            <LoadingSpinner>
                              <ProgressSpinner
                                style={{ width: "12px", height: "12px" }}
                                strokeWidth="4"
                              />
                            </LoadingSpinner>
                          )}
                        </RecommendedCptLink>
                      ) : (
                        <RecommendedCptText>
                          {recommendation.cptCode}
                        </RecommendedCptText>
                      )}{" "}
                      <span className="text-gray-500">
                        ({recommendation.occurrenceRate}%)
                      </span>
                    </span>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </RecommendationsContainer>
  );
}
